import PresentationStoreModule from '@app/store/modules/PresentationStoreModule';
import IImage from '@app/types/data/base/IImage';
import IChapterOverviewSlideData from '@app/types/data/slides/IChapterOverviewSlideData';
import ISlideView from '@app/types/ISlideView';
import ChapterItemType from '@app/types/presentations/ChapterItemType';
import IChapterItem from '@app/types/presentations/IChapterItem';
import ISectionMaster from '@app/types/presentations/master/ISectionMaster';
import ISlideMaster from '@app/types/presentations/master/ISlideMaster';
import IChapterSettings from '@app/types/presentations/presentation/IChapterSettings';
import IPresentationSettings from '@app/types/presentations/presentation/IPresentationSettings';
import SlideType from '@app/types/presentations/SlideType';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import INavigationItem from '../../types/data/base/INavigationItem';
import AbstractSlideTransform, { thumbnailCropOptions } from './AbstractSlideTransform';

/**
 * Transform for 006-ChapterOverviewSlide
 */
export default class ChapterOverviewSlideTransform extends AbstractSlideTransform {
	public viewComponent: string = 'ChapterOverviewSlide';

	public async transform(
		slide: ISlideMaster,
		presentationSettings: IPresentationSettings
	): Promise<IChapterOverviewSlideData> {
		const presentationStore: PresentationStoreModule = getStoreModule(PresentationStoreModule);

		const image = this.transformImageAsset(slide.fields.image?.fields.asset ?? this.getFallbackImage(), {
			width: 5000,
			height: 5000,
			crop: 'limit'
		});

		let logo: IImage | undefined;

		if (slide.fields.logo && slide.fields.logo.fields.asset) {
			logo = {
				...this.transformImageAsset(slide.fields.logo.fields.asset, {
					width: 390,
					height: 250,
					crop: 'limit'
				}),
				alt: slide.fields.logo.fields.title
			};
		}

		const data: IChapterOverviewSlideData = {
			id: slide.id,
			slideId: slide.id,
			label: slide.fields.headline,
			thumbnail: this.extractThumbnail(slide.fields.headline, slide, 'image'),
			image,
			logo,
			headline: slide.fields.headline,
			elevation: slide.fields.elevation,
			operatingTime: typeof slide.fields.operatingTime === 'object' ? undefined : slide.fields.operatingTime,
			operatingTimes: AbstractSlideTransform.parseObjectWithOperatingTimes(slide),
			navigationItems: []
		};

		if (slide.fields.videoLoop && slide.fields.videoLoop.fields.asset) {
			data.videoOverview = this.transformVideoAsset(slide.fields.videoLoop.fields.asset);
		}

		if (slide.fields.videoDetail && slide.fields.videoDetail.fields.asset) {
			data.videoDetail = this.transformVideoAsset(slide.fields.videoDetail.fields.asset, undefined, 'auto');
		}

		const chapterId: string = Object.keys(presentationStore.slideIdsByChapterId).find(
			// eslint-disable-next-line @typescript-eslint/no-shadow
			(chapterId: string): boolean => presentationStore.slideIdsByChapterId[chapterId].includes(slide.id)
		) as string;

		const chapterSettings: IChapterSettings = presentationSettings.chapters.find(
			// eslint-disable-next-line @typescript-eslint/no-shadow
			(chapterSettings: IChapterSettings) => chapterSettings.id === chapterId
		) as IChapterSettings;

		if (slide.fields.videoIntro && slide.fields.videoIntro.fields.asset && chapterSettings.showIntroVideo) {
			data.videoIntro = this.transformVideoAsset(slide.fields.videoIntro.fields.asset);
		}

		const chapterItems: IChapterItem[] = presentationStore.selectedChapterItemsByChapterId[chapterId];
		data.navigationItems = (
			await Promise.all(
				chapterItems.map(async (item: IChapterItem): Promise<INavigationItem | undefined> => {
					if (item.type === ChapterItemType.Section) {
						// item is a section - use label and thumbnail from sectionMaster, but link to first section slide
						const masterSection: ISectionMaster = presentationStore.master.sectionsById[item.id];

						const firstSelectedSectionSlideId: string = presentationStore.selectedSlideIds.find(
							(slideId: string): boolean => masterSection.slides.includes(slideId)
						) as string;

						// add first section slide, but ensure we're not adding the chapter overview slide in itself
						if (firstSelectedSectionSlideId !== slide.id) {
							return {
								id: firstSelectedSectionSlideId,
								label: masterSection.title,
								image: this.transformImageAsset(masterSection.thumbnail, thumbnailCropOptions)
							};
						}
						// item is a slide, but we need to ensure we're not adding the chapter overview slide in itself
					} else if (item.id !== slide.id) {
						// verify we're not dealing with another ChapterOverviewSlide in the same chapter:
						if (
							presentationStore.master.slidesById[item.id] &&
							presentationStore.master.slidesById[item.id].type === SlideType.ChapterOverviewSlide
						) {
							console.error(
								`More than one chapter overview slide found in chapter  https://app.contentful.com/spaces/${process.env.CONTENTFUL_SPACE_ID}/entries/${chapterId}`
							);

							return undefined;
						}

						// item is a slide
						const slideView: ISlideView = await presentationStore.slideViewPromisesById[item.id];

						return {
							id: item.id,
							label: slideView.data.label,
							image: slideView.data.thumbnail
						};
					}

					return undefined;
				})
			)
		).filter((navigationItem: INavigationItem | undefined): navigationItem is INavigationItem => !!navigationItem);

		return data;
	}
}
